/**
 * FondeoPage — On-demand funding with master-detail layout.
 * Left: pending credits box + confirmed funding history
 * Right: credit detail for selected item
 * Modal: confirm funding with SPEI reference
 */

import { useState, useMemo } from 'react';
import {
  RefreshCw,
  Banknote,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES, PARTNER_ID, UNE_PERM } from '@/constants';
import { Can } from '@/components/auth/Can';
import { useAuth } from '@/hooks/useAuth';
import { FundingCreditsTable } from '@/features/funding/components/FundingCreditsTable';
import {
  getPendingCredits,
  createFundingInstruction,
  registerFundingSent,
  getFundingHistory,
} from '@/services/credit-payment.service';

// ─── Constants ──────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FONDEO },
];

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'CONFIRMED': return 'success';
    case 'SENT': return 'info';
    case 'PENDING': return 'warning';
    default: return 'default';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'CONFIRMED': return 'Confirmado';
    case 'SENT': return 'Enviado';
    case 'PENDING': return 'Pendiente';
    default: return status;
  }
}

interface CreditItem {
  credit_id: string;
  directory_id: string;
  amount_mxn: number;
  disbursed_at?: string;
  phone_number?: string | null;
  credit_level?: string | null;
  /** Pendiente backend — ver PENDIENTES.md item 004. */
  full_name?: string | null;
}

interface FundingHistoryItem {
  date: string;
  total_amount_mxn: number;
  credits_count: number;
  status: string;
  bank_reference?: string;
  confirmed_at?: string;
  funding_id?: string;
  instructions: CreditItem[];
}

// ─── Confirm Funding Modal ─────────────────────────────────────────────────

function ConfirmFundingModal({ totalMxn, creditsCount, onConfirm, onCancel, isProcessing }: {
  totalMxn: number;
  creditsCount: number;
  onConfirm: (reference: string) => void;
  onCancel: () => void;
  isProcessing: boolean;
}) {
  const [reference, setReference] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-foreground">Confirmar Fondeo</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Se creara una instruccion de fondeo por {creditsCount} credito{creditsCount !== 1 ? 's' : ''}.
        </p>

        <div className="mt-4 rounded-md border border-border bg-muted/30 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Monto total</span>
            <span className="text-xl font-bold tabular-nums text-foreground">{formatCurrency(totalMxn, 'MXN', 'es-MX')}</span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Creditos</span>
            <span className="text-sm font-medium text-foreground">{creditsCount}</span>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-sm font-medium text-foreground">Referencia SPEI</label>
          <Input
            placeholder="Ej: SPEI-20260402-001"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="mt-1"
            autoFocus
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isProcessing}>Cancelar</Button>
          <Button onClick={() => onConfirm(reference)} disabled={!reference.trim() || isProcessing}>
            {isProcessing ? 'Procesando...' : 'Confirmar Fondeo'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Left: Pending Box ─────────────────────────────────────────────────────

function PendingBox({ totalMxn, creditsCount, isSelected, onClick, onFundClick }: {
  totalMxn: number;
  creditsCount: number;
  isSelected: boolean;
  onClick: () => void;
  onFundClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
        isSelected
          ? 'border-primary bg-primary/5'
          : creditsCount > 0
            ? 'border-amber-300 bg-amber-50 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'
            : 'border-green-300 bg-green-50 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/20 dark:hover:bg-green-900/30'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pendiente de Fondeo</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{formatCurrency(totalMxn, 'MXN', 'es-MX')}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{creditsCount} credito{creditsCount !== 1 ? 's' : ''}</p>
        </div>
        {creditsCount > 0 && (
          <Can permission={UNE_PERM.FUNDING_EXECUTE}>
            <Button
              onClick={(e) => { e.stopPropagation(); onFundClick(); }}
              className="shrink-0"
            >
              <Banknote size={16} /> Fondear
            </Button>
          </Can>
        )}
      </div>
    </div>
  );
}

// ─── Left: History List ────────────────────────────────────────────────────

function HistoryList({ items, selectedId, onSelect }: {
  items: FundingHistoryItem[];
  selectedId: string | null;
  onSelect: (item: FundingHistoryItem) => void;
}) {
  if (items.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Sin fondeos confirmados</p>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Fecha</th>
          <th className="px-2 py-1.5 text-center font-medium">Cred</th>
          <th className="px-2 py-1.5 text-right font-medium">Monto</th>
          <th className="px-2 py-1.5 font-medium">Referencia</th>
          <th className="px-2 py-1.5 text-center font-medium">Estado</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const id = item.funding_id || item.date;
          const selected = id === selectedId;
          return (
            <tr
              key={id}
              onClick={() => onSelect(item)}
              className={`cursor-pointer border-b border-border/20 transition-colors ${selected ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
              style={{ height: 32 }}
            >
              <td className="px-3 py-0 text-xs text-foreground">{formatDateShort(item.date)}</td>
              <td className="px-2 py-0 text-center tabular-nums text-xs text-foreground">{item.credits_count}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs font-medium text-foreground">{formatCurrency(item.total_amount_mxn, 'MXN', 'es-MX')}</td>
              <td className="px-2 py-0 text-xs text-muted-foreground font-mono">{item.bank_reference?.slice(0, 15) ?? '-'}</td>
              <td className="px-2 py-0 text-center">
                <Badge variant={getStatusVariant(item.status)} className="text-[9px]">{getStatusLabel(item.status)}</Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Right: Credit Detail Table ────────────────────────────────────────────

function CreditDetailTable({ credits, title }: { credits: CreditItem[]; title: string }) {
  return <FundingCreditsTable credits={credits} title={title} />;
}

// ─── Main Page ──────────────────────────────────────────────────────────────

type Selection = { type: 'pending' } | { type: 'history'; item: FundingHistoryItem };

export function FondeoPage() {
  const { user } = useAuth();

  const { data: pendingData, refetch: refetchPending } = useQuery({
    queryKey: ['funding', 'pending-credits'],
    queryFn: () => getPendingCredits(PARTNER_ID),
  });

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['funding', 'history'],
    queryFn: () => getFundingHistory(90),
  });

  const pendingCredits: CreditItem[] = pendingData?.data?.credits ?? [];
  const pendingTotal: number = pendingData?.data?.total_pending_mxn ?? 0;
  const history: FundingHistoryItem[] = historyData?.data ?? [];





  const [selection, setSelection] = useState<Selection>({ type: 'pending' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Detail credits for right panel
  const detailCredits: CreditItem[] = useMemo(() => {
    if (selection.type === 'pending') return pendingCredits;
    const instructions = selection.item.instructions;
    if (!instructions || !Array.isArray(instructions)) return [];
    return instructions.filter((c: any) => c && c.credit_id);
  }, [selection, pendingCredits]);

  const detailTitle = selection.type === 'pending'
    ? 'Creditos pendientes de fondeo'
    : `Fondeo ${formatDateShort(selection.item.date)}`;

  const handleFund = async (reference: string) => {
    setIsProcessing(true);
    try {
      // 1. Create instruction
      const createResp = await createFundingInstruction(PARTNER_ID);
      const fundingId = createResp?.data?.funding_id;

      if (fundingId) {
        // 2. UNE-Credito registers SPEI sent (NOT confirm — FiadoSA confirms later)
        await registerFundingSent(fundingId, {
          bankReference: reference,
          sentBy: user?.email ?? 'admin',
        });
      }

      // Refresh both panels
      await Promise.all([refetchPending(), refetchHistory()]);
      setShowConfirmModal(false);
      setSelection({ type: 'pending' });
    } catch (error) {
      console.error('Error processing funding', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const refetchAll = () => { void refetchPending(); void refetchHistory(); };

  return (
    <div>
      <PageHeader
        title="Desembolsos"
        helpKey="funding"
        subtitle="Creditos desembolsados pendientes de reposicion por UNE-Credito"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={refetchAll}><RefreshCw size={16} /> Actualizar</Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[500px_1fr]">
        {/* ── Left Panel ──────────────────────────────────────────── */}
        <div className="space-y-4">
          <PendingBox
            totalMxn={pendingTotal}
            creditsCount={pendingCredits.length}
            isSelected={selection.type === 'pending'}
            onClick={() => setSelection({ type: 'pending' })}
            onFundClick={() => setShowConfirmModal(true)}
          />

          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="border-b border-border bg-muted/30 px-3 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fondeos Confirmados</h3>
            </div>
            <HistoryList
              items={history}
              selectedId={selection.type === 'history' ? (selection.item.funding_id ?? selection.item.date) : null}
              onSelect={(item) => setSelection({ type: 'history', item })}
            />
          </div>
        </div>

        {/* ── Right Panel ─────────────────────────────────────────── */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <CreditDetailTable credits={detailCredits} title={detailTitle} />
        </div>
      </div>

      {/* ── Confirm Modal ───────────────────────────────────────── */}
      {showConfirmModal && (
        <ConfirmFundingModal
          totalMxn={pendingTotal}
          creditsCount={pendingCredits.length}
          onConfirm={handleFund}
          onCancel={() => setShowConfirmModal(false)}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}
